// Quiz service for enhanced quiz functionality
import api from "./api";
import {
  Quiz,
  QuizSchedule,
  Question,
  QuizAttempt,
  QuizAnalytics,
} from "../types/quiz";

export interface CreateQuizRequest {
  title: string;
  description?: string;
  instructions?: string;
  courseId: string;
  courseType?: "course" | "ecourse";
  moduleId?: string;
  timeLimit?: number;
  attempts?: number;
  passingScore?: number;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  showResults?: boolean;
  showCorrectAnswers?: boolean;
  settings?: any;
  grading?: any;
  schedule?: QuizSchedule;
  questions?: Question[];
}

export interface GetQuizzesParams {
  courseId?: string;
  page?: number;
  limit?: number;
  status?: "published" | "draft" | "archived";
  search?: string;
}

export interface QuizzesResponse {
  quizzes: Quiz[];
  total: number;
  page: number;
  totalPages: number;
}

export interface QuizFormData {
  title: string;
  description?: string;
  instructions?: string;
  courseId: string;
  courseType?: "course" | "ecourse";
  moduleId?: string;
  timeLimit?: number;
  attempts?: number;
  passingScore?: number;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  showResults?: boolean;
  showCorrectAnswers?: boolean;
  settings?: any;
  grading?: any;
  schedule?: QuizSchedule;
  questions?: Question[];
}

// Helper to normalize response bodies. Our `api` instance returns `response.data` already,
// but backend sometimes nests the payload under `.data` or `.data.data`.
const unwrap = (resp: any) => {
  if (resp == null) return resp;
  // If server wrapped object: { success, message, data: { ...quiz } }
  if (resp.data !== undefined) {
    // If nested further: { data: { data: {...} } }
    if (resp.data && resp.data.data !== undefined) return resp.data.data;
    return resp.data;
  }
  return resp;
};

export const quizService = {
  // Create quiz with enhanced scheduling
  async createQuiz(quizData: CreateQuizRequest): Promise<Quiz> {
    try {
      const response = await api.post("/elearning/quizzes", quizData);
      return unwrap(response) as any;
    } catch (error: any) {
      // Extract specific error message from API response
      const errorData = error.response?.data;
      if (errorData?.message) {
        // If there are validation errors array, format them nicely
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const validationMessages = errorData.errors.map((err: any) => {
            if (err.questionIndex) {
              return `Question ${err.questionIndex}: ${
                err.errors?.join(", ") || err.message
              }`;
            }
            return err.message || err;
          });
          throw new Error(
            `${errorData.message}\n${validationMessages.join("\n")}`
          );
        }
        // Return the specific backend error message
        throw new Error(errorData.message);
      }
      // Fallback to generic error
      throw error;
    }
  },

  // Get quizzes for teacher
  async getTeacherQuizzes(params?: GetQuizzesParams): Promise<QuizzesResponse> {
    const response = await api.get("/elearning/quizzes", { params });
    return unwrap(response) as any;
  },

  // Get quiz with availability info
  async getQuiz(quizId: string): Promise<Quiz> {
    const response = await api.get(`/elearning/quizzes/${quizId}`);
    return unwrap(response) as any;
  },

  // Backwards-compatible alias used by some legacy components
  async getQuizById(quizId: string): Promise<{ data: Quiz }> {
    const q = await this.getQuiz(quizId);
    return { data: q } as any;
  },

  // Get quiz for student (with availability checks)
  async getQuizForStudent(quizId: string): Promise<Quiz> {
    const response = await api.get(`/elearning/quizzes/${quizId}`);
    return unwrap(response) as any;
  },

  // Update quiz
  async updateQuiz(quizId: string, updates: Partial<Quiz>): Promise<Quiz> {
    const response = await api.put(`/elearning/quizzes/${quizId}`, updates);
    return unwrap(response) as any;
  },

  // Update quiz schedule
  async updateSchedule(quizId: string, schedule: QuizSchedule): Promise<Quiz> {
    const response = await api.put(`/elearning/quizzes/${quizId}/schedule`, {
      schedule,
    });
    return unwrap(response) as any;
  },

  // Delete quiz
  async deleteQuiz(quizId: string): Promise<void> {
    await api.delete(`/elearning/quizzes/${quizId}`);
  },

  // Add questions to quiz
  async addQuestions(quizId: string, questions: Question[]): Promise<void> {
    await api.post(`/elearning/quizzes/${quizId}/questions`, { questions });
  },

  // Backwards-compatible single-add
  async addQuestion(quizId: string, question: Question): Promise<void> {
    await this.addQuestions(quizId, [question]);
  },

  // Update question in quiz
  async updateQuestion(
    quizId: string,
    questionId: string,
    questionData: Partial<Question>
  ): Promise<void> {
    await api.put(
      `/elearning/quizzes/${quizId}/questions/${questionId}`,
      questionData
    );
  },

  // Delete question from quiz
  async deleteQuestion(quizId: string, questionId: string): Promise<void> {
    await api.delete(`/elearning/quizzes/${quizId}/questions/${questionId}`);
  },

  // Backwards-compatible removeQuestion alias
  async removeQuestion(quizId: string, questionId: string): Promise<void> {
    await this.deleteQuestion(quizId, questionId);
  },

  // Import questions
  async importQuestions(
    quizId: string,
    questions: any[],
    format: "json" | "csv" | "qti" = "json"
  ): Promise<void> {
    await api.post(`/elearning/quizzes/${quizId}/import`, {
      questions,
      format,
    });
  },

  // Export questions
  async exportQuestions(
    quizId: string,
    format: "json" | "csv" | "qti" = "json"
  ): Promise<Blob> {
    const response = await api.get(`/elearning/quizzes/${quizId}/export`, {
      params: { format },
      responseType: "blob",
    });
    return response.data;
  },

  // Get student quizzes
  async getStudentQuizzes(params?: GetQuizzesParams): Promise<Quiz[]> {
    const response = await api.get("/elearning/student-quizzes", { params });
    return unwrap(response) as Quiz[];
  },

  // Publish/unpublish quiz
  async togglePublishStatus(quizId: string): Promise<Quiz> {
    const response = await api.post(
      `/elearning/quizzes/${quizId}/toggle-publish`
    );
    return unwrap(response) as any;
  },

  // Duplicate quiz
  async duplicateQuiz(quizId: string): Promise<Quiz> {
    const response = await api.post(`/elearning/quizzes/${quizId}/duplicate`);
    return unwrap(response) as any;
  },

  // Quiz Attempts
  async startQuizAttempt(quizId: string): Promise<QuizAttempt> {
    const response = await api.post(`/elearning/quizzes/${quizId}/attempts`);
    return unwrap(response) as any;
  },

  async getQuizAttempt(attemptId: string): Promise<QuizAttempt> {
    const response = await api.get(`/elearning/quizzes/attempts/${attemptId}`);
    return unwrap(response) as any;
  },

  async submitAnswer(
    attemptId: string,
    questionId: string,
    answer: any
  ): Promise<void> {
    await api.post(`/elearning/quizzes/attempts/${attemptId}/answers`, {
      questionId,
      answer,
    });
  },

  async submitQuizAttempt(attemptId: string): Promise<QuizAttempt> {
    const response = await api.post(
      `/elearning/quizzes/attempts/${attemptId}/submit`
    );
    return unwrap(response) as any;
  },

  async getStudentAttempts(quizId: string): Promise<QuizAttempt[]> {
    const response = await api.get(`/elearning/quizzes/${quizId}/my-attempts`);
    return unwrap(response) as any;
  },

  async getQuizAttempts(
    quizId: string,
    params?: {
      page?: number;
      limit?: number;
      studentId?: string;
    }
  ): Promise<{ attempts: QuizAttempt[]; total: number }> {
    const response = await api.get(`/elearning/quizzes/${quizId}/attempts`, {
      params,
    });
    return unwrap(response) as any;
  },

  // Analytics
  async getQuizAnalytics(quizId: string): Promise<QuizAnalytics> {
    const response = await api.get(`/elearning/quizzes/${quizId}/analytics`);
    return unwrap(response) as any;
  },

  async getDetailedQuizReport(quizId: string): Promise<any> {
    const response = await api.get(
      `/elearning/quizzes/${quizId}/detailed-report`
    );
    return unwrap(response) as any;
  },

  // Grading
  async gradeQuizAttempt(
    attemptId: string,
    grades: Record<string, number>,
    feedback?: Record<string, string>
  ): Promise<any> {
    const response = await api.post(
      `/elearning/quizzes/attempts/${attemptId}/grade`,
      {
        grades,
        feedback: feedback || {},
      }
    );
    return unwrap(response) as any;
  },

  async gradeEssayQuestion(
    attemptId: string,
    questionId: string,
    score: number,
    feedback?: string
  ): Promise<void> {
    await api.post(`/elearning/quiz-attempts/${attemptId}/grade`, {
      questionId,
      score,
      feedback,
    });
  },

  async bulkGradeAttempts(
    quizId: string,
    grades: Array<{
      attemptId: string;
      scores: Array<{ questionId: string; score: number; feedback?: string }>;
    }>
  ): Promise<void> {
    await api.post(`/elearning/quizzes/${quizId}/bulk-grade`, { grades });
  },

  // Get default quiz form data
  getDefaultQuizFormData(): any {
    return {
      title: "",
      description: "",
      instructions: "Read each question carefully and select the best answer.",
      courseId: "",
      courseType: "ecourse",
      moduleId: "",
      timeLimit: 0,
      attempts: 1,
      passingScore: 60,
      shuffleQuestions: false,
      shuffleOptions: false,
      showResults: true,
      showCorrectAnswers: true,
      settings: {
        randomizeQuestions: false,
        oneQuestionAtATime: false,
        preventBacktracking: false,
        lockQuestionsAfterAnswering: false,
        requireWebcam: false,
        fullScreenMode: false,
        showProgressBar: true,
      },
      grading: {
        autoGrade: true,
        gradingMethod: "highest",
        releaseGrades: "immediately",
      },
      schedule: {
        availableFrom: undefined,
        availableUntil: undefined,
        dueDate: undefined,
      },
      questions: [],
    };
  },

  // Backwards-compatible helper expected by older components
  getDefaultQuestionData(type: string): any {
    const base: any = {
      type,
      question: "",
      points: 1,
      explanation: "",
      difficulty: "medium",
      options: [],
      correctAnswer: "",
      correctAnswers: [],
      rubric: [],
      pairs: [],
      tags: [],
      estimatedTime: 60,
    };

    if (type === "multiple_choice") base.options = ["", "", "", ""];
    if (type === "true_false") base.options = ["True", "False"];
    if (type === "matching")
      base.pairs = [
        { left: "", right: "" },
        { left: "", right: "" },
      ];

    return base;
  },

  // Toggle quiz publication status
  async toggleQuizPublication(
    quizId: string,
    isPublished: boolean
  ): Promise<Quiz> {
    const response = await api.patch(`/elearning/quizzes/${quizId}/publish`, {
      isPublished,
    });
    return unwrap(response) as any;
  },

  // Archive a quiz
  async archiveQuiz(quizId: string): Promise<Quiz> {
    const response = await api.put(`/elearning/quizzes/${quizId}/archive`);
    return unwrap(response) as any;
  },

  // Unarchive a quiz
  async unarchiveQuiz(quizId: string): Promise<Quiz> {
    const response = await api.put(`/elearning/quizzes/${quizId}/unarchive`);
    return unwrap(response) as any;
  },
};

export default quizService;
